import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  ScrollText, ChevronLeft, ChevronRight, User, Clock,
  Plus, Pencil, Trash2, Eye, Search, Download, X,
  ChevronUp, ChevronDown, ChevronsUpDown, Settings2,
} from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useAppearance } from "../contexts/AppearanceContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { formatDateTimeSeconds } from "../lib/dateUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const ACTION_CONFIG = {
  create: { label: "Criação", color: "bg-emerald-100 text-emerald-700", icon: Plus },
  update: { label: "Edição", color: "bg-sky-100 text-sky-700", icon: Pencil },
  delete: { label: "Exclusão", color: "bg-rose-100 text-rose-700", icon: Trash2 },
};

function formatDate(isoStr) {
  return formatDateTimeSeconds(isoStr) || "-";
}

function DataDiff({ label, data }) {
  if (!data || Object.keys(data).length === 0) return null;
  return (
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{label}</p>
      <div className="bg-slate-50 rounded-lg p-3 space-y-1 max-h-60 overflow-auto">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="flex gap-2 text-sm">
            <span className="text-slate-500 font-mono text-xs min-w-[120px]">{key}:</span>
            <span className="text-slate-800 font-mono text-xs break-all">{typeof value === "object" ? JSON.stringify(value) : String(value ?? "-")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AuditoriaPage() {
  const { t } = useTranslation();
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#06B6D4";
  const tc50 = appearance.colors?.["50"] || "#ECFEFF";

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalLogs, setTotalLogs] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Search
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  // Sort
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  // Selection & column visibility
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => { setSearchDebounced(search); setPage(0); }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audit-logs`, {
        params: { page: page + 1, limit: PAGE_SIZE },
        withCredentials: true,
      });
      setLogs(res.data.logs || []);
      setTotalLogs(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { setRowSelection({}); }, [logs]);

  // Client-side filter + sort (API doesn't support search/sort params)
  const filtered = useMemo(() => {
    let data = logs;
    if (searchDebounced) {
      const s = searchDebounced.toLowerCase();
      data = data.filter(l =>
        l.description?.toLowerCase().includes(s) ||
        l.user_name?.toLowerCase().includes(s) ||
        l.module?.toLowerCase().includes(s) ||
        l.action?.toLowerCase().includes(s)
      );
    }
    data = [...data].sort((a, b) => {
      const av = (a[sortBy] || "").toString().toLowerCase();
      const bv = (b[sortBy] || "").toString().toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return data;
  }, [logs, searchDebounced, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(totalLogs / PAGE_SIZE));

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronsUpDown className="w-3.5 h-3.5 text-slate-300" />;
    return sortDir === "asc" ? <ChevronUp className="w-3.5 h-3.5 text-slate-700" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-700" />;
  };

  // Columns
  const columns = useMemo(() => [
    {
      id: "select",
      size: 40,
      enableSorting: false,
      header: ({ table }) => (
        <Checkbox
          data-testid="select-all-audit"
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(v) => row.toggleSelected(!!v)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      id: "action",
      accessorKey: "action",
      header: "Ação",
      size: 110,
      cell: ({ row }) => {
        const config = ACTION_CONFIG[row.original.action] || ACTION_CONFIG.create;
        const ActionIcon = config.icon;
        return (
          <Badge className={`border-0 text-xs gap-1 ${config.color}`}>
            <ActionIcon className="w-3 h-3" />{config.label}
          </Badge>
        );
      },
    },
    {
      id: "module",
      accessorKey: "module",
      header: "Módulo",
      size: 120,
      cell: ({ getValue }) => (
        <span className="text-xs font-mono uppercase text-slate-500">{getValue()}</span>
      ),
    },
    {
      id: "description",
      accessorKey: "description",
      header: "Descrição",
      size: 320,
      cell: ({ getValue }) => (
        <span className="text-sm text-slate-800 truncate block max-w-[300px]">{getValue()}</span>
      ),
    },
    {
      id: "user_name",
      accessorKey: "user_name",
      header: "Usuário",
      size: 160,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold" style={{ backgroundColor: tc500 }}>
            {row.original.user_name?.charAt(0).toUpperCase() || "?"}
          </div>
          <span className="text-sm text-slate-600 truncate">{row.original.user_name}</span>
        </div>
      ),
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Data/Hora",
      size: 170,
      cell: ({ getValue }) => (
        <span className="text-sm text-slate-500">{formatDate(getValue())}</span>
      ),
    },
    {
      id: "actions",
      size: 50,
      enableSorting: false,
      header: "",
      cell: ({ row }) => (
        <Tooltip><TooltipTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-sky-600"
            onClick={(e) => { e.stopPropagation(); setSelectedLog(row.original); }}>
            <Eye className="w-3.5 h-3.5" />
          </Button>
        </TooltipTrigger><TooltipContent>Detalhes</TooltipContent></Tooltip>
      ),
    },
  ], [tc500]);

  const table = useReactTable({
    data: filtered,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    state: { rowSelection, columnVisibility },
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true,
    getRowId: (row) => row.id,
  });

  const toggleableColumns = useMemo(() => {
    return columns.filter(c => c.id !== "select" && c.id !== "actions").map(c => ({
      id: c.id, label: c.header || c.id,
    }));
  }, [columns]);

  // Export
  const handleExport = async () => {
    try {
      const XLSX = await import("xlsx");
      const rows = filtered.map(l => ({
        "Ação": (ACTION_CONFIG[l.action] || ACTION_CONFIG.create).label,
        "Módulo": l.module,
        "Descrição": l.description,
        "Usuário": l.user_name,
        "Email": l.user_email,
        "Data": formatDate(l.created_at),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = Object.keys(rows[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 20) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Auditoria");
      XLSX.writeFile(wb, `auditoria_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) { console.error(err); }
    setExportDialogOpen(false);
  };

  return (
    <div data-testid="auditoria-page" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
            <ScrollText className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
              {t("configuracoes.auditoria.title")}
              <Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{totalLogs}</Badge>
            </h1>
            <p className="text-slate-500 mt-0.5">{t("configuracoes.auditoria.subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="audit-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar registro..." className="pl-9 w-full sm:w-56 h-9 text-sm" />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button data-testid="audit-col-visibility" variant="outline" size="icon" className="h-9 w-9 text-slate-600">
                <Settings2 className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 mb-2">Colunas visíveis</p>
              {toggleableColumns.map(col => {
                const isVisible = table.getColumn(col.id)?.getIsVisible() ?? true;
                return (
                  <label key={col.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm text-slate-700">
                    <Checkbox checked={isVisible} onCheckedChange={(v) => table.getColumn(col.id)?.toggleVisibility(!!v)} />
                    {col.label}
                  </label>
                );
              })}
            </PopoverContent>
          </Popover>
          <Tooltip><TooltipTrigger asChild>
            <Button data-testid="audit-export-btn" variant="outline" size="icon" className="h-9 w-9 text-slate-600" onClick={() => setExportDialogOpen(true)}>
              <Download className="w-4 h-4" />
            </Button>
          </TooltipTrigger><TooltipContent>{t("ui.exportar")}</TooltipContent></Tooltip>
        </div>
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id} className="border-b border-slate-200 bg-slate-50/80">
                  {hg.headers.map(header => {
                    const canSort = header.column.columnDef.enableSorting !== false && header.column.columnDef.accessorKey;
                    return (
                      <th key={header.id}
                        style={{ width: header.getSize() }}
                        className={`px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${canSort ? "cursor-pointer select-none hover:text-slate-700" : ""}`}
                        onClick={() => canSort && handleSort(header.column.columnDef.accessorKey)}>
                        <div className="flex items-center gap-1.5">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && <SortIcon col={header.column.columnDef.accessorKey} />}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    {columns.filter(c => table.getColumn(c.id)?.getIsVisible() !== false).map((c, j) => (
                      <td key={j} className="px-3 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" style={{ width: `${40 + Math.random() * 40}%` }} /></td>
                    ))}
                  </tr>
                ))
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-16">
                    <div className="flex flex-col items-center text-slate-400">
                      <ScrollText className="w-10 h-10 mb-3 text-slate-300" />
                      <p className="text-sm">{t("ui.nenhum_registro_auditoria")}</p>
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.map(row => (
                <tr key={row.id}
                  data-testid={`audit-row-${row.original.id}`}
                  className={`border-b border-slate-100 transition-colors cursor-pointer ${row.getIsSelected() ? "bg-slate-50" : "hover:bg-slate-50/50"}`}
                  onClick={() => setSelectedLog(row.original)}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="px-3 py-2.5">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
          <p className="text-xs text-slate-500">
            {totalLogs > 0 ? `${page * PAGE_SIZE + 1}-${Math.min((page + 1) * PAGE_SIZE, totalLogs)} de ${totalLogs}` : "Nenhum resultado"}
          </p>
          <div className="flex items-center gap-1">
            <Button data-testid="audit-prev-page" variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i;
              else if (page < 3) pageNum = i;
              else if (page > totalPages - 4) pageNum = totalPages - 5 + i;
              else pageNum = page - 2 + i;
              return (
                <Button key={pageNum} variant={pageNum === page ? "default" : "outline"} size="sm"
                  className={`h-8 w-8 p-0 text-xs ${pageNum === page ? "text-white" : ""}`}
                  style={pageNum === page ? { backgroundColor: tc500 } : {}}
                  onClick={() => setPage(pageNum)}>
                  {pageNum + 1}
                </Button>
              );
            })}
            <Button data-testid="audit-next-page" variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ScrollText className="w-5 h-5" style={{ color: tc500 }} />{t("ui.detalhes_auditoria")}</DialogTitle></DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{t("ui.acao")}</p>
                  <Badge className={`border-0 ${(ACTION_CONFIG[selectedLog.action] || ACTION_CONFIG.create).color}`}>{(ACTION_CONFIG[selectedLog.action] || ACTION_CONFIG.create).label}</Badge>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{t("ui.modulo")}</p>
                  <p className="text-sm font-medium text-slate-900 font-mono uppercase">{selectedLog.module}</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{t("common.description")}</p>
                <p className="text-sm text-slate-800">{selectedLog.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{t("ui.usuario")}</p>
                  <p className="text-sm font-medium text-slate-900">{selectedLog.user_name}</p>
                  <p className="text-xs text-slate-500">{selectedLog.user_email}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Data/Hora</p>
                  <p className="text-sm font-medium text-slate-900">{formatDate(selectedLog.created_at)}</p>
                </div>
              </div>
              {selectedLog.record_id && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">ID do Registro</p>
                  <p className="text-sm text-slate-700 font-mono">{selectedLog.record_id}</p>
                </div>
              )}
              <DataDiff label="Dados Anteriores" data={selectedLog.data_before} />
              <DataDiff label="Dados Atuais" data={selectedLog.data_after} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2"><Download className="w-5 h-5" style={{ color: tc500 }} />Exportar Auditoria</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">Deseja fazer download de todas as informações da listagem em formato de planilha?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-slate-600"><X className="w-4 h-4 mr-2" />{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleExport} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90"><Download className="w-4 h-4 mr-2" />{t("ui.baixar_excel")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
